import Link from 'next/link'

import { cn } from '@/lib/utils'

// 5x5 pixel glyphs rendered inside the sliding box.
// 1 = bright pixel, 0 = dim pixel. The "k" is the Kingdom Kode mark.
const GLYPHS = {
  k: [
    [1, 0, 0, 1, 0],
    [1, 0, 1, 0, 0],
    [1, 1, 0, 0, 0],
    [1, 0, 1, 0, 0],
    [1, 0, 0, 1, 0],
  ],
  arrow: [
    [0, 0, 1, 0, 0],
    [0, 0, 0, 1, 0],
    [1, 1, 1, 1, 1],
    [0, 0, 0, 1, 0],
    [0, 0, 1, 0, 0],
  ],
  // Three-point crown with a jewel gap in the band.
  crown: [
    [1, 0, 1, 0, 1],
    [1, 1, 1, 1, 1],
    [1, 1, 0, 1, 1],
    [1, 1, 1, 1, 1],
    [0, 0, 0, 0, 0],
  ],
}

export function PixelGlyph({ glyph, hoverGlyph }) {
  const grid = GLYPHS[glyph] ?? GLYPHS.k
  const hoverGrid = hoverGlyph ? GLYPHS[hoverGlyph] : null
  return (
    // Counter-rotates so the glyph stays upright while the box spins on hover.
    <span className="flex flex-col gap-px transition-transform duration-400 ease-out group-hover:rotate-180">
      {grid.map((row, r) => (
        <span key={r} className="flex gap-px">
          {row.map((on, c) => {
            const hoverOn = hoverGrid ? hoverGrid[r][c] : on
            return (
              <span
                key={c}
                // Staggered per-pixel color morph: the base glyph dissolves
                // into the hover glyph pixel by pixel.
                style={hoverGrid ? { transitionDelay: `${(r * 5 + c) * 14}ms` } : undefined}
                className={cn(
                  'inline-block size-0.75 shrink-0 rounded-full transition-colors duration-300',
                  on ? 'bg-white' : 'bg-white/25',
                  hoverGrid &&
                    (hoverOn
                      ? 'group-hover:bg-kingdom-gold'
                      : 'group-hover:bg-white/15'),
                )}
              />
            )
          })}
        </span>
      ))}
    </span>
  )
}

/**
 * Kingdom Kode animated "pixel reaction" button.
 * The glyph box slides across and rotates on hover while the label slides
 * aside. The gold "k" box darkens on hover while its pixels morph into a
 * gold crown.
 *
 * @param {'k'|'arrow'|'crown'} glyph - pixel art shown in the box
 * @param {'k'|'arrow'|'crown'|null} [hoverGlyph] - pixel art the glyph morphs
 *   into on hover (defaults to a crown for the "k" glyph)
 * @param {'gold'|'muted'} tone - box/label treatment
 * @param {string} [href]      - renders a Link when provided
 * @param {'submit'|'button'} [type] - renders a real <button> when provided
 * @param {boolean} [disabled] - button variant only
 * @param {boolean} [stationary] - hover animates ONLY the pixel icon (rotate,
 *   darken, crown morph) — the box stays put and the label never moves
 */
export function PixelButton({
  text,
  children,
  href,
  glyph = 'k',
  hoverGlyph = glyph === 'k' ? 'crown' : null,
  tone = 'gold',
  className,
  type,
  disabled = false,
  onClick,
  stationary = false,
}) {
  const label = children ?? text
  const muted = tone === 'muted'

  const inner = (
    <>
      <span
        className={cn(
          'absolute inset-y-0 left-1 z-10 my-auto flex size-8 items-center justify-center rounded-[5px] transition-all duration-400 ease-out group-hover:rotate-180',
          !stationary && 'group-hover:left-[calc(100%-2.3rem)]',
          muted ? 'bg-neutral-600' : 'bg-kingdom-gold group-hover:bg-neutral-800',
        )}
      >
        <PixelGlyph glyph={glyph} hoverGlyph={hoverGlyph} />
      </span>
      {!stationary && (
        <span className="pointer-events-none absolute -inset-px rounded-lg bg-white/15 transition-[clip-path] duration-400 ease-out [clip-path:inset(0_100%_0_0)] group-hover:[clip-path:inset(0_0%_0_0)]" />
      )}
      <span
        className={cn(
          'relative inline-block transition-transform duration-400 ease-out',
          !stationary && 'group-hover:-translate-x-8',
          muted ? 'text-neutral-400' : 'text-white',
        )}
      >
        {label}
      </span>
    </>
  )

  const base =
    'group relative flex w-fit items-center gap-2 overflow-hidden rounded-lg border border-white/15 bg-neutral-950 py-2 pr-4 pl-11 text-sm font-semibold tracking-tight shadow-[0_12px_28px_rgba(10,10,10,0.18)]'

  if (href) {
    return (
      <Link href={href} className={cn(base, className)}>
        {inner}
      </Link>
    )
  }

  if (type || onClick) {
    return (
      <button
        type={type || 'button'}
        onClick={onClick}
        disabled={disabled}
        className={cn(
          base,
          'cursor-pointer',
          // pointer-events-none also freezes the hover animation while disabled.
          disabled && 'pointer-events-none opacity-40',
          className,
        )}
      >
        {inner}
      </button>
    )
  }

  return (
    <span className={cn(base, 'cursor-default select-none', className)}>
      {inner}
    </span>
  )
}
